import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isSameDay, startOfWeek, endOfWeek } from 'date-fns';
import { EventBadge } from './EventBadge';
import { useEventsForRange, useEventPriorityBadge } from '@/hooks/useEventsForRange';

interface GregorianGridProps {
  currentDate: Date;
  selectedDate: Date | null;
  onSelectDate: (date: Date) => void;
  liveDate?: Date;
  widgetSize?: { width: number; height: number }; // Dashboard widget dimensions (grid cols/rows)
  dashboardId?: string; // For namespacing events to shared dashboards
}

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export function GregorianGrid({ currentDate, selectedDate, onSelectDate, liveDate, widgetSize, dashboardId }: GregorianGridProps) {
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = gridRef.current;
    if (!node) return;
    const update = () => {
      const height = node.clientHeight || 0;
      const daySize = Math.max(14, Math.min(30, height / 6 - 4));
      node.style.setProperty('--calendar-day-size', `${daySize}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(node);
    return () => ro.disconnect();
  }, [currentDate]);

  // When widget is 1x1 (minimum size), show only the day number
  const isMinimalMode = widgetSize && widgetSize.width <= 1 && widgetSize.height <= 1;
  
  if (isMinimalMode && selectedDate) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl font-bold text-primary mb-2">{format(selectedDate, 'd')}</div>
          <p className="text-xs text-muted-foreground">
            {format(selectedDate, 'MMM')}
          </p>
        </div>
      </div>
    );
  }
  
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  const today = new Date();
  
  // Fetch events for the visible month
  const eventsByDate = useEventsForRange(monthStart, monthEnd, dashboardId);

  const isToday = (date: Date) => isSameDay(date, today);
  const isSelected = (date: Date) => selectedDate && isSameDay(date, selectedDate);
  const isLive = (date: Date) => liveDate && isSameDay(date, liveDate);
  const isCurrentMonth = (date: Date) => date.getMonth() === currentDate.getMonth();

  const getDayStatusLabel = (date: Date) => {
    const statuses: string[] = [];

    if (isToday(date)) {
      statuses.push('Today');
    }

    if (isSelected(date)) {
      statuses.push('Selected date');
    }

    if (isLive(date)) {
      statuses.push('Live date indicator');
    }

    if (!isCurrentMonth(date)) {
      statuses.push('Adjacent month');
    }

    const baseLabel = format(date, 'MMMM d, yyyy');

    if (!statuses.length) {
      return `${baseLabel} • Select to view details`;
    }

    return `${baseLabel} • ${statuses.join(' • ')}`;
  };

  return (
    <div className="flex h-full w-full flex-col">
      <div className="mb-0.5 grid w-full grid-cols-7 gap-0.5">
        {WEEKDAY_LABELS.map((name) => (
          <div
            key={name}
            className="py-0 text-center text-[8px] font-medium uppercase tracking-[0.12em] text-white/32"
          >
            {name}
          </div>
        ))}
      </div>
      <div
        ref={gridRef}
        className="grid w-full flex-1 min-h-0 auto-rows-fr grid-cols-7 gap-0.5"
      >
        {days.map((day) => {
          const dateKey = day.toISOString().split('T')[0];
          const dayEvents = eventsByDate[dateKey] || [];
          const { color, count, statuses } = useEventPriorityBadge(dayEvents);

          return (
            <button
              key={day.toISOString()}
              onClick={() => onSelectDate(day)}
              title={getDayStatusLabel(day)}
              aria-label={getDayStatusLabel(day)}
              className={cn(
                'calendar-day relative aspect-square h-full w-full overflow-hidden rounded-[14px] border border-white/5 bg-white/[0.03] text-[clamp(0.7rem,2.1vw,0.94rem)] font-medium text-white/86 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                isToday(day) && 'today border-cyan-300/35 bg-cyan-300/8 text-white',
                isSelected(day) && 'border-[#f6f0c9] bg-[#f5efcf] text-slate-950 shadow-[0_8px_18px_rgba(0,0,0,0.18)]',
                isLive(day) && !isSelected(day) && 'ring-2 ring-fuchsia-300/60 ring-offset-1 ring-offset-transparent',
                !isCurrentMonth(day) && 'border-white/3 bg-white/[0.02] text-white/28',
                !isToday(day) && !isSelected(day) && isCurrentMonth(day) && 'hover:border-white/20 hover:bg-white/[0.08]'
              )}
            >
              {isToday(day) && !isSelected(day) ? (
                <span className="absolute left-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-cyan-200/90" />
              ) : null}
              {isSelected(day) ? (
                <span className="absolute left-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-slate-950/70" />
              ) : null}
              {!isCurrentMonth(day) ? (
                <span className="absolute inset-0 bg-slate-950/12" />
              ) : null}
              <div className="relative flex h-full items-center justify-center p-1">
                <span
                  className="leading-none"
                  style={{
                    lineHeight: 1,
                    fontSize: isSelected(day)
                      ? 'calc(var(--calendar-day-size, 1rem) * 1.03)'
                      : 'var(--calendar-day-size, 1rem)',
                  }}
                >
                  {format(day, 'd')}
                </span>
              </div>
              {dayEvents.length > 0 ? (
                <span
                  className={cn(
                    'absolute bottom-1 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full',
                    isSelected(day) ? 'bg-slate-950/55' : 'bg-cyan-200/75',
                  )}
                />
              ) : null}
              <div className="absolute inset-0">
                <EventBadge color={color} count={count} statuses={statuses} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
